import { useState, useCallback } from 'react'
import type { UserSettings } from '@fuelfinder/shared'

/**
 * localStorage key for settings
 */
const SETTINGS_STORAGE_KEY = 'fuelfinder-settings'

/**
 * Default settings
 */
const DEFAULT_SETTINGS: UserSettings = {
  fuelType: 'E10',
  sortBy: 'cheapest',
  radiusMiles: 5,
  typicalSpendPounds: 40,
}

/**
 * Load settings from localStorage, falling back to defaults
 */
function loadSettings(): UserSettings {
  try {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<UserSettings>
      // Merge with defaults to handle missing/new fields
      return {
        ...DEFAULT_SETTINGS,
        ...parsed,
      }
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_SETTINGS
}

/**
 * Save settings to localStorage
 */
function saveSettings(settings: UserSettings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Ignore storage errors
  }
}

interface UseSettingsReturn {
  settings: UserSettings
  updateSettings: (partial: Partial<UserSettings>) => void
}

/**
 * Hook for managing user settings with localStorage persistence
 */
export function useSettings(): UseSettingsReturn {
  const [settings, setSettings] = useState<UserSettings>(() => loadSettings())

  const updateSettings = useCallback((partial: Partial<UserSettings>) => {
    setSettings((current) => {
      const updated = { ...current, ...partial }
      saveSettings(updated)
      return updated
    })
  }, [])

  return {
    settings,
    updateSettings,
  }
}
